<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { onDestroy, untrack } from 'svelte';
    import { themeHandler as themeHandlerFactory } from '@oat-sa-private/ui-core';
    import ColourSwitcher from './generic/ColourSwitcher.svelte';
    import {
        applyA11yContrastVars,
        clearA11yContrastVars
    } from 'taoQtiNuiItem/runner/item/stylesheets/a11yCustomStyles.js';
    // Constants
    import settingsKeys from '../../../settings/settingsKeys.js';
    import { defaultThemeKey, themeLabels, themeColours, isThemeDark } from './contrastThemeConstants.js';

    /**
     * @typedef {Object} Props
     * @property {Object} areaBroker
     * @property {Object} [config]
     * @property {String[]} [config.themes] - keys of enabled themes
     * @property {Object} initialState
     * @property {Function} onChange
     */
    /** @type {Props} */
    // prettier-ignore
    let {
        areaBroker,
        config = { themes: [] },
        initialState,
        onChange
    } = $props();

    // svelte-ignore state_referenced_locally
    const targetElt = areaBroker?.getContainer();

    // svelte-ignore state_referenced_locally
    let themes = config.themes;
    if (!themes.includes(defaultThemeKey)) {
        themes = [defaultThemeKey, ...themes];
    }

    // TODO: load CSS in this component rather than passing 'stylesheet' to themeHandler?
    // because 'stylesheet' path depends on rollup-plugin-copy to move files into place
    const normalizedThemes = Object.keys(themeLabels)
        .filter(key => themes.includes(key))
        .map(key => ({
            // properties for Dropdown
            key,
            label: themeLabels[key],
            colour: themeColours[key],
            // properties for themeHandler
            id: key,
            stylesheet: key === defaultThemeKey ? null : `themes/${key}.css`
        }));

    // svelte-ignore state_referenced_locally
    const themeScope = areaBroker?.getContainer();
    const themeHandler = themeHandlerFactory(normalizedThemes, {
        /**
         * @type {HTMLElement} themeScope - the one element to which the root data-theme attribute will be set.
         * If some child areas should be unthemed, they should be rendered with data-theme="default"
         * to stop the root theme cascading into them.
         */
        themeScope,
        isThemeDark
    });
    themeHandler.load();

    let value = $state(null);

    const initialStateOnce = untrack(() => initialState);
    if (initialStateOnce?.value) {
        handleThemeChange(initialStateOnce.value);
    }

    /**
     * Trigger theme change
     * @param {Object} [newValue]
     * @param {String} [newValue.value] - theme identifier
     * @param {Object} [newValue.position] - {x, y}
     */
    function handleThemeChange(newValue = {}) {
        value = newValue;
        themeHandler.activate(value.value);

        if (targetElt) {
            if (value.value !== defaultThemeKey) {
                applyA11yContrastVars(targetElt);
            } else {
                clearA11yContrastVars(targetElt);
            }
        }

        onChange?.({
            key: settingsKeys.contrastTheme,
            state: {
                value,
                nonDefault: value.value !== defaultThemeKey
            }
        });
    }

    onDestroy(() => {
        themeHandler.destroy();
        clearA11yContrastVars(targetElt);
    });
</script>

<style>
    @import '../themes/themeColours.css';

    .contrast-theme-setting {
        & :global(.select .preview) {
            display: inline-block;
            width: 100%;
            background: var(--color-bg-default, white);
            color: var(--color-text-default, black);
        }
        & :global(.select .listbox .option) {
            padding: 0;
        }
        & :global(.select.lite button) {
            border-bottom: 0;
        }
        & :global(.colour-switcher) {
            margin-left: -1rem;
            margin-right: -1rem;
        }
    }
</style>

<div class="contrast-theme-setting" class:changed-control={value?.value !== 'default'}>
    <ColourSwitcher
        value={value?.value}
        options={normalizedThemes}
        defaultOptionKey={defaultThemeKey}
        onChange={handleThemeChange}
        name="contrast-theme-picker" />
</div>
